// EDEN · the teacher's own activities, links and files on a plan (teacher review, 15 Oct 2026, point 10).
//
// "Some teachers want to teach differently — their own activities, their Kahoot or Bamboozle games — so the plan
// should let them type their activities, or even upload their PowerPoints and materials, so the tracking system knows
// they do prepare." This card lists what the teacher adds — an activity (with a link if it has one), a link, a file
// (PowerPoint, PDF, Word, pictures, audio) or the book pages they chose in the materials panel — and the Lead sees the
// same list on the plan. It never writes into the plan's text boxes (Module D rails); it is a list beside them.
//
// API: functions/api/lessons/[id]/resources/** (migration 0035). Files go to the private R2 bucket eden-uploads; when
// that bucket is not switched on yet the server says so (503 uploads_off) and the card offers a link instead.

import { api, ApiError } from "./api";
import { esc, notify } from "./dom";
import { icon } from "./icons";
import { STEPS, type StepKey } from "./plan-steps";

export interface PlanResource {
  id: string;
  lesson_id: string;
  kind: "activity" | "link" | "file" | "book";
  title: string;
  activity_type: string | null;
  detail: string | null;
  url: string | null;
  minutes: number | null;
  step: StepKey | null;
  book_id: string | null;
  pages: string | null;
  file: { name: string; type: string; size: number; href: string } | null;
  position: number;
  created_at: string;
  can_edit: boolean;
}

export const ACTIVITY_TYPES: { key: string; label: string }[] = [
  { key: "game", label: "Game" },
  { key: "kahoot", label: "Kahoot" },
  { key: "bamboozle", label: "Bamboozle" },
  { key: "wordwall", label: "Wordwall" },
  { key: "quizizz", label: "Quizizz" },
  { key: "song", label: "Song or chant" },
  { key: "video", label: "Video" },
  { key: "roleplay", label: "Role-play" },
  { key: "worksheet", label: "My own worksheet" },
  { key: "other", label: "Other" },
];
const typeLabel = (k: string | null): string => ACTIVITY_TYPES.find((t) => t.key === k)?.label ?? "";
const stepLabel = (k: string | null): string => {
  const s = STEPS.find((x) => x.key === k);
  return s ? s.label.replace(/^\d\s*/, "") : "";
};
export const UPLOAD_LIMIT_MB = 25;
const ACCEPT = ".pdf,.ppt,.pptx,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.webp,.mp3,.m4a,.wav,.mp4";

export interface ResourcesContext {
  /** The lesson id once the plan exists (a new plan creates it on its first save). */
  lessonId: () => string | null;
  /** Create the lesson now if it does not exist yet (a new plan with a topic). Throws with a plain message if it cannot. */
  ensureLesson: () => Promise<string>;
  /** False on an approved (locked) plan or a reader who is not the plan's writer. */
  canEdit: boolean;
}

export interface ResourcesHandle {
  hasUnsavedChanges(): boolean;
  /** Open the add form, optionally for one step of the procedure. */
  openAdd(kind: "activity" | "link" | "file", step?: StepKey | null): void;
  /** Add the week's book pages (from the materials panel). */
  addBook(b: { book_id: string; pages: string; title: string }): Promise<void>;
  reload(): Promise<void>;
}

/** The card's frame — the host places it in its form; mountResources() fills it. */
export function resourcesCardHtml(): string {
  return `<section class="card form-card pr" id="own-materials" aria-labelledby="pr-h" data-pr>
    <h2 class="card-title" id="pr-h">Materials for this lesson</h2>
    <p class="hint">Add your slides, files, links or activities here. Your Lead can read this list on your plan.</p>
    <p data-pr-load role="status" hidden></p>
    <button type="button" class="btn secondary sm" data-pr-retry hidden>Retry loading materials</button>
    <ul class="pr-list" data-pr-list></ul>
    <div class="pr-books" data-pr-books hidden>
      <h3 class="pr-sub">Book pages added to this plan</h3>
      <p class="hint small">Pages of the department's books, added from the Book tab. They are on your plan, but they are not counted as your own materials.</p>
      <ul class="pr-list" data-pr-book-list></ul>
    </div>
    <div class="pr-add" data-pr-add>
      <button type="button" class="btn secondary sm" data-pr-new="activity">${icon("plus", "ico-sm")}<span>Activity or game</span></button>
      <button type="button" class="btn secondary sm" data-pr-new="link">${icon("external", "ico-sm")}<span>Add a video or Drive link</span></button>
      <button type="button" class="btn secondary sm" data-pr-new="file">${icon("download", "ico-sm")}<span>Add slides or a file</span></button>
    </div>
    <div class="pr-form-holder" data-pr-form hidden></div>
    <p class="pr-msg small" data-pr-msg role="status" hidden></p>
  </section>`;
}

function kindIcon(r: PlanResource): string {
  if (r.kind === "file") return icon(/pdf/.test(r.file?.type ?? "") ? "pdf" : "sheet");
  if (r.kind === "link") return icon("external");
  if (r.kind === "book") return icon("book");
  return icon(r.activity_type === "song" || r.activity_type === "video" ? "audio" : "puzzle");
}

function sizeLabel(n: number): string {
  return n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`;
}

/** Read-only list for the plan page (teacher and Lead). */
export function resourcesListHtml(list: PlanResource[], opts: { editable?: boolean } = {}): string {
  if (!list.length) return "";
  return list.map((r) => {
    const meta = [
      r.kind === "activity" ? typeLabel(r.activity_type) || "Activity" : r.kind === "link" ? "Link" : r.kind === "file" ? `File · ${esc(r.file?.name ?? "")} · ${sizeLabel(r.file?.size ?? 0)}` : "Book pages",
      r.minutes ? `${r.minutes} min` : "",
      r.step ? `in ${stepLabel(r.step)}` : "",
    ].filter(Boolean).join(" · ");
    const open = r.kind === "file" && r.file
      ? (/^(application\/pdf|image\/)/.test(r.file.type)
        // a PDF opens in EDEN's own viewer; a picture opens in a new tab (the viewer draws PDFs only)
        ? (r.file.type === "application/pdf"
          ? `<a class="btn secondary sm" href="${esc(r.file.href)}?inline=1" data-doc-open="pdf" data-doc-title="${esc(r.title)}">${icon("eye", "ico-sm")}<span>Open</span></a>`
          : `<a class="btn secondary sm" href="${esc(r.file.href)}?inline=1" target="_blank" rel="noopener" data-no-viewer>${icon("eye", "ico-sm")}<span>Open</span></a>`) : "")
        + `<a class="btn secondary sm" href="${esc(r.file.href)}" download data-no-viewer>${icon("download", "ico-sm")}<span>Download</span></a>`
      : r.url ? `<a class="btn secondary sm" href="${esc(r.url)}" target="_blank" rel="noopener noreferrer" data-no-viewer>${icon("external", "ico-sm")}<span>Open link</span></a>` : "";
    const edit = opts.editable && r.can_edit
      ? `${r.kind === "activity" || r.kind === "link" ? `<button type="button" class="linkbtn small" data-pr-edit="${esc(r.id)}">Change</button>` : ""}<button type="button" class="linkbtn small" data-pr-del="${esc(r.id)}">Remove</button>`
      : "";
    return `<li class="pr-item" data-pr-id="${esc(r.id)}">
      <span class="pr-ico" aria-hidden="true">${kindIcon(r)}</span>
      <div class="pr-body"><b>${esc(r.title)}</b><span class="pr-meta">${meta}</span>${r.detail ? `<span class="pr-detail">${esc(r.detail)}</span>` : ""}</div>
      <div class="pr-actions">${open}${edit}</div>
    </li>`;
  }).join("");
}

export async function loadResources(lessonId: string): Promise<PlanResource[] | null> {
  try {
    return (await api<{ resources: PlanResource[] }>(`/api/lessons/${encodeURIComponent(lessonId)}/resources`)).resources;
  } catch {
    return null; // callers display failure separately from an empty saved list
  }
}

export function mountResources(root: HTMLElement, ctx: ResourcesContext): ResourcesHandle {
  const card = root.querySelector<HTMLElement>("[data-pr]")!;
  const listEl = card.querySelector<HTMLElement>("[data-pr-list]")!;
  const booksEl = card.querySelector<HTMLElement>("[data-pr-books]")!;
  const formEl = card.querySelector<HTMLElement>("[data-pr-form]")!;
  const msg = card.querySelector<HTMLElement>("[data-pr-msg]")!;
  let items: PlanResource[] = [];
  let editing: PlanResource | null = null;
  let formBaseline = "";
  const formState = (): string => JSON.stringify([...formEl.querySelectorAll("input,select,textarea")].map((el) => (el as HTMLInputElement).value));
  if (!ctx.canEdit) card.querySelector<HTMLElement>("[data-pr-add]")!.hidden = true;

  function say(text: string): void {
    msg.hidden = !text;
    msg.textContent = text;
  }

  function paint(): void {
    // The teacher's own activities, links and files — what the Lead's pages count — and, apart, the department's book
    // pages added from the Book tab (on the plan, never counted as the teacher's own).
    const own = items.filter((r) => r.kind !== "book");
    const books = items.filter((r) => r.kind === "book");
    listEl.innerHTML = own.length ? resourcesListHtml(own, { editable: ctx.canEdit }) : `<li class="pr-empty muted small">Nothing added yet.</li>`;
    booksEl.querySelector<HTMLElement>("[data-pr-book-list]")!.innerHTML = resourcesListHtml(books, { editable: ctx.canEdit });
    booksEl.hidden = !books.length;
    // the chips under each step of the procedure
    root.querySelectorAll<HTMLElement>("[data-step-chips]").forEach((box) => {
      const mine = items.filter((r) => r.step === box.dataset.stepChips);
      box.innerHTML = mine.map((r) => `<span class="pr-chip">${kindIcon(r)}<span>${esc(r.title)}${r.minutes ? ` · ${r.minutes} min` : ""}</span></span>`).join("");
      box.hidden = !mine.length;
    });
  }

  async function reload(): Promise<void> {
    const status = card.querySelector<HTMLElement>("[data-pr-load]")!;
    const retry = card.querySelector<HTMLButtonElement>("[data-pr-retry]")!;
    const id = ctx.lessonId();
    if (!id) { items = []; paint(); return; }
    status.hidden = false;
    status.textContent = "Loading saved materials…";
    retry.hidden = true;
    const got = await loadResources(id);
    if (got) { items = got; paint(); status.hidden = true; }
    else {
      status.textContent = "Saved materials could not be loaded. Try again; anything already shown is the last loaded list.";
      retry.hidden = false;
    }
  }
  card.querySelector("[data-pr-retry]")!.addEventListener("click", () => void reload());

  function closeForm(): void {
    formEl.hidden = true;
    formEl.innerHTML = "";
    editing = null;
  }

  function openAdd(kind: "activity" | "link" | "file", step: StepKey | null = null, r: PlanResource | null = null): void {
    if (!ctx.canEdit) return;
    editing = r;
    say("");
    const stepOpts = `<option value="">Any part of the lesson</option>${STEPS.map((s) => `<option value="${s.key}"${(r?.step ?? step) === s.key ? " selected" : ""}>${esc(s.label.replace(/^\d\s*/, ""))}</option>`).join("")}`;
    const title = r?.title ?? "";
    let fields = "";
    if (kind === "activity") {
      fields = `
        <label for="pr-type">What kind of activity?</label>
        <select id="pr-type">${ACTIVITY_TYPES.map((t) => `<option value="${t.key}"${(r?.activity_type ?? "game") === t.key ? " selected" : ""}>${esc(t.label)}</option>`).join("")}</select>
        <label for="pr-title">Name it</label>
        <input id="pr-title" maxlength="120" value="${esc(title)}" placeholder="e.g. Kahoot quiz: weather words" />
        <div class="form-grid">
          <label for="pr-min">Minutes (optional)<input id="pr-min" type="number" min="1" max="180" step="1" value="${r?.minutes ?? ""}" /></label>
          <label for="pr-step">Which part of the lesson?<select id="pr-step">${stepOpts}</select></label>
        </div>
        <label for="pr-url">Link (optional)</label>
        <input id="pr-url" type="url" maxlength="500" value="${esc(r?.url ?? "")}" placeholder="https://…" />
        <label for="pr-detail">How you will use it (optional)</label>
        <textarea id="pr-detail" maxlength="1000" class="pr-detail-box" placeholder="e.g. Teams of four. The winning team chooses the next song.">${esc(r?.detail ?? "")}</textarea>`;
    } else if (kind === "link") {
      fields = `
        <p class="hint">For Google Drive, share the file with your Lead's Google account or school group. EDEN cannot verify Google access. Ask your Lead to open the saved link to check it. Linked files may change or be removed.</p>
        <label for="pr-url">The link</label>
        <input id="pr-url" type="url" maxlength="500" value="${esc(r?.url ?? "")}" placeholder="https://…" />
        <label for="pr-title">Name it</label>
        <input id="pr-title" maxlength="120" value="${esc(title)}" placeholder="e.g. My slides on Google Drive" />
        <label for="pr-step">Which part of the lesson?</label><select id="pr-step">${stepOpts}</select>
        <label for="pr-detail">Note (optional)</label>
        <textarea id="pr-detail" maxlength="1000" class="pr-detail-box">${esc(r?.detail ?? "")}</textarea>`;
    } else {
      fields = `
        <label for="pr-file">Choose the file</label>
        <input id="pr-file" type="file" accept="${ACCEPT}" />
        <p class="hint">PowerPoint, PDF, Word, Excel, pictures, audio or MP4 — up to ${UPLOAD_LIMIT_MB} MB per file, 30 materials per lesson. PowerPoint downloads for opening in your presentation app. For a bigger video add a Drive link.</p>
        <label for="pr-title">Name it (optional)</label>
        <input id="pr-title" maxlength="120" placeholder="e.g. Weather slides" />
        <label for="pr-step">Which part of the lesson?</label><select id="pr-step">${stepOpts}</select>
        <div class="pr-progress" data-pr-progress hidden><span></span></div>`;
    }
    formEl.innerHTML = `<div class="pr-form" data-kind="${kind}">
      <p class="pr-form-title">${kind === "activity" ? (r ? "Change this activity" : "Add an activity or game") : kind === "link" ? (r ? "Change this link" : "Add a link") : "Add a file"}</p>
      ${fields}
      <p class="error small" data-pr-err hidden></p>
      <div class="pr-form-actions">
        <button type="button" class="btn sm" data-pr-save>${icon("check", "ico-sm")}<span>${kind === "file" ? "Upload" : "Save"}</span></button>
        <button type="button" class="btn secondary sm" data-pr-cancel>Cancel</button>
      </div>
    </div>`;
    formEl.hidden = false;
    formBaseline = formState();
    formEl.querySelector<HTMLElement>(kind === "file" ? "#pr-file" : kind === "link" ? "#pr-url" : "#pr-title")?.focus();
    formEl.querySelector<HTMLButtonElement>("[data-pr-cancel]")!.addEventListener("click", closeForm);
    formEl.querySelector<HTMLButtonElement>("[data-pr-save]")!.addEventListener("click", () => void save(kind));
    formEl.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }

  const val = (sel: string): string => (formEl.querySelector(sel) as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null)?.value.trim() ?? "";
  function fail(text: string): void {
    const e = formEl.querySelector<HTMLElement>("[data-pr-err]");
    if (e) { e.textContent = text; e.hidden = false; }
  }

  async function save(kind: "activity" | "link" | "file"): Promise<void> {
    const btn = formEl.querySelector<HTMLButtonElement>("[data-pr-save]")!;
    btn.disabled = true;
    try {
      if (kind === "file") return await upload();
      const url = val("#pr-url");
      let title = val("#pr-title");
      if (kind === "link" && !url) return fail("Paste or type the link first.");
      if (url && !/^https?:\/\//i.test(url)) return fail("A link starts with https:// — copy it from the address bar.");
      if (kind === "link" && !title) title = linkName(url);
      if (!title) return fail("Give it a short name, so you and your Lead can find it.");
      const minutes = Number(val("#pr-min")) || null;
      const body = {
        kind, title, url: url || null, detail: val("#pr-detail") || null, step: (val("#pr-step") || null) as StepKey | null,
        ...(kind === "activity" ? { activity_type: val("#pr-type") || "other", minutes } : {}),
      };
      const lesson = await ctx.ensureLesson();
      const changing = !!editing; // closeForm() forgets which one was being changed
      if (editing) {
        await api(`/api/lessons/${encodeURIComponent(lesson)}/resources/${encodeURIComponent(editing.id)}`, { method: "PATCH", body: JSON.stringify(body) });
      } else {
        await api(`/api/lessons/${encodeURIComponent(lesson)}/resources`, { method: "POST", body: JSON.stringify(body) });
      }
      closeForm();
      await reload();
      notify(changing ? "Changed." : "Added to your plan.");
    } catch (error) {
      fail((error as Error).message || "That did not save. Check the connection and try again.");
    } finally {
      btn.disabled = false;
    }
  }

  async function upload(): Promise<void> {
    const input = formEl.querySelector<HTMLInputElement>("#pr-file");
    const file = input?.files?.[0];
    if (!file) return fail("Choose a file first.");
    if (file.size > UPLOAD_LIMIT_MB * 1024 * 1024) return fail(`This file is ${sizeLabel(file.size)}. The limit is ${UPLOAD_LIMIT_MB} MB — save it smaller, or add it as a link (Google Drive, OneDrive).`);
    let lesson: string;
    try { lesson = await ctx.ensureLesson(); } catch (e) { return fail((e as Error).message); }
    const bar = formEl.querySelector<HTMLElement>("[data-pr-progress]");
    if (bar) bar.hidden = false;
    const result = await new Promise<{ status: number; body: { error?: string; code?: string } }>((resolve) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `/api/lessons/${encodeURIComponent(lesson)}/resources/upload`);
      xhr.withCredentials = true;
      xhr.setRequestHeader("Content-Type", file.type || "application/octet-stream");
      xhr.setRequestHeader("X-File-Name", encodeURIComponent(file.name));
      const title = val("#pr-title");
      if (title) xhr.setRequestHeader("X-Title", encodeURIComponent(title));
      const step = val("#pr-step");
      if (step) xhr.setRequestHeader("X-Step", step);
      xhr.upload.onprogress = (e) => { if (bar && e.lengthComputable) bar.querySelector<HTMLElement>("span")!.style.width = `${Math.round((e.loaded / e.total) * 100)}%`; };
      xhr.onload = () => { let b = {}; try { b = JSON.parse(xhr.responseText || "{}"); } catch { /* not JSON */ } resolve({ status: xhr.status, body: b }); };
      xhr.onerror = () => resolve({ status: 0, body: {} });
      xhr.send(file);
    });
    if (bar) bar.hidden = true;
    if (result.status === 201 || result.status === 200) {
      closeForm();
      await reload();
      notify("Uploaded. Your Lead can see it on your plan.");
      return;
    }
    if (result.body.code === "uploads_off") return fail("File upload is not switched on yet. Add your slides as a link instead (Google Drive or OneDrive → Share → Copy link).");
    if (result.status === 0) return fail("The upload did not finish. Check the internet connection and try again.");
    fail(result.body.error || "The upload did not work. Try again, or add the file as a link.");
  }

  card.addEventListener("click", (e) => {
    const t = e.target as HTMLElement;
    const add = t.closest<HTMLElement>("[data-pr-new]");
    if (add) return openAdd(add.dataset.prNew as "activity" | "link" | "file");
    const edit = t.closest<HTMLElement>("[data-pr-edit]");
    if (edit) {
      const r = items.find((x) => x.id === edit.dataset.prEdit);
      if (r && (r.kind === "activity" || r.kind === "link")) openAdd(r.kind, r.step, r);
      return;
    }
    const del = t.closest<HTMLElement>("[data-pr-del]");
    if (del) {
      const r = items.find((x) => x.id === del.dataset.prDel);
      if (!r) return;
      // A quiet two-step remove, never a browser dialog (dialogs block the page).
      if (del.dataset.confirm !== "1") { del.dataset.confirm = "1"; del.textContent = "Remove? Tap again"; return; }
      void (async () => {
        try {
          await api(`/api/lessons/${encodeURIComponent(r.lesson_id)}/resources/${encodeURIComponent(r.id)}`, { method: "DELETE" });
          await reload();
          notify("Removed.");
        } catch (error) { say((error as Error).message); }
      })();
    }
  });
  // "＋ Add an activity" under each step of the procedure
  root.addEventListener("click", (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>("[data-add-activity]");
    if (!b) return;
    openAdd("activity", (b.dataset.addActivity || null) as StepKey | null);
  });

  void reload();
  return {
    hasUnsavedChanges: () => !formEl.hidden && formState() !== formBaseline,
    openAdd: (kind, step) => openAdd(kind, step ?? null),
    async addBook(b) {
      try {
        const lesson = await ctx.ensureLesson();
        if (items.some((r) => r.kind === "book" && r.book_id === b.book_id && r.pages === b.pages)) { notify("These pages are already on your plan."); return; }
        await api(`/api/lessons/${encodeURIComponent(lesson)}/resources`, { method: "POST", body: JSON.stringify({ kind: "book", ...b }) });
        await reload();
        notify("Added to your plan — see “Book pages added to this plan”.");
      } catch (error) {
        notify(error instanceof ApiError && error.status === 404 ? "This server cannot keep book pages on a plan yet." : (error as Error).message);
      }
    },
    reload,
  };
}

function linkName(url: string): string {
  try {
    const host = new URL(url).hostname.replace(/^www\./, "");
    const known: Record<string, string> = { "kahoot.it": "Kahoot", "create.kahoot.it": "Kahoot", "baamboozle.com": "Bamboozle", "wordwall.net": "Wordwall", "quizizz.com": "Quizizz", "youtube.com": "YouTube video", "youtu.be": "YouTube video", "docs.google.com": "Google document", "drive.google.com": "Google Drive file", "onedrive.live.com": "OneDrive file", "canva.com": "Canva design" };
    return known[host] ?? host;
  } catch { return ""; }
}
